// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { PageContent } from '@/components/page-content';
import {
  Sort,
  SortDirection,
  invert as invertDirection,
  sortDirectionSchema,
} from '@/constants/sort';
import { useLocalStorage } from '@/hooks/use-local-storage';
import { ReturnOrderListing } from '@/return-order/components/return-order-listing';
import {
  ReturnOrderSearchListing,
  ReturnOrderSortProperty,
  returnOrderSearchListingSchema,
} from '@/return-order/hooks/use-return-orders';
import { createFileRoute } from '@tanstack/react-router';
import { z } from 'zod';

/**
 * Local storage key for persisting sort preferences.
 */
const LOCAL_STORAGE_SORT_KEY = 'return-order-sort';

/**
 * Default sort property when no preference is set.
 */
const DEFAULT_SORT_PROPERTY: ReturnOrderSortProperty = 'roNumber';

/**
 * Default sort direction when no preference is set.
 */
const DEFAULT_SORT_DIRECTION: SortDirection = 'asc';

/**
 * Schema for validating sort preferences.
 */
const sortSchema = z.object({
  property: z.enum(['roNumber', 'status']),
  direction: sortDirectionSchema,
});

/**
 * Route configuration for return order listing page.
 */
export const Route = createFileRoute('/_app/return-orders/')({
  component: ReturnOrderRoute,
  validateSearch: returnOrderSearchListingSchema,
});

/**
 * Main component for the return order listing page.
 */
function ReturnOrderRoute() {
  const navigate = Route.useNavigate();
  const search = Route.useSearch();

  /**
   * Sort preference persistence in local storage.
   */
  const [sortPreference, setSortPreference] = useLocalStorage(
    LOCAL_STORAGE_SORT_KEY,
    sortSchema
  );

  /**
   * Current sort configuration.
   * Prioritizes URL parameters over local storage preferences.
   */
  const sort: Sort<ReturnOrderSortProperty> = {
    property: search.sort ?? sortPreference?.property ?? DEFAULT_SORT_PROPERTY,
    direction:
      search.direction ?? sortPreference?.direction ?? DEFAULT_SORT_DIRECTION,
  };

  /**
   * Updates sort configuration.
   * Saves preference to local storage and updates URL.
   */
  const setSort = (
    property: ReturnOrderSortProperty,
    direction: SortDirection
  ) => {
    setSortPreference({ property, direction });
    navigate({ search: (prev) => ({ ...prev, direction, sort: property }) });
  };

  /**
   * Handles column header click for sorting.
   * Toggles direction if same column, otherwise sorts ascending.
   */
  const handleSort = (property: ReturnOrderSortProperty) => {
    if (property === sort.property) {
      return setSort(property, invertDirection(sort.direction));
    }
    setSort(property, 'asc');
  };

  const searchParameters: ReturnOrderSearchListing = {
    page: search.page,
    query: search.query,
    status: search.status,
    subStatuses: search.subStatuses,
    reason: search.reason,
    sort: search.sort,
    direction: search.direction,
    fromLocations: search.fromLocations,
    fromCustomerLocations: search.fromCustomerLocations,
    fromLocationCompanyTypes: search.fromLocationCompanyTypes,
    toLocations: search.toLocations,
    toCustomerLocations: search.toCustomerLocations,
    toLocationCompanyTypes: search.toLocationCompanyTypes,
  };

  return (
    <PageContent>
      <ReturnOrderListing
        currentStatus={search.status ?? 'all'}
        searchParameters={searchParameters}
        sort={sort}
        onSort={handleSort}
        navigateFn={navigate}
      />
    </PageContent>
  );
}
